import type { GolfAuthNavLogoVariant } from './GolfAuthNavLogo';
import { cn } from '../lib/cn';

export interface GolfAuthLoadingScreenProps {
  /** Caption beneath the spinner. Default: "Loading…" */
  message?: string;
  /** Logo variant — kept for backward compatibility with callers that
   *  pass 'pro'. The loading mark is currently the same SVG for both
   *  variants; the prop is accepted but ignored. */
  variant?: GolfAuthNavLogoVariant;
  /** Render as an in-tab centred block instead of taking the full
   *  viewport. Use inside Pro admin tabs / nested layouts where a
   *  fullscreen takeover would hide the surrounding navigation. */
  inline?: boolean;
  /** Layout overrides. Sizing comes from inline/fullscreen mode. */
  className?: string;
}

/**
 * Canonical GolfAuth loading state. Asset 7 SVG mark above a brand-
 * tinted spinner above a short caption. Drop-in replacement for
 * ad-hoc "Loading…" text strings — gives every screen the same
 * identity while it waits.
 *
 * Asset 7.svg ships from each app's /public/ directory; the encoded
 * path /Asset%207.svg resolves on both app.golfauth.com and
 * golfauth.com.
 */
export function GolfAuthLoadingScreen({
  message = 'Loading…',
  inline = false,
  className,
}: GolfAuthLoadingScreenProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-4',
        // Default (non-inline) sizing: viewport-fixed overlay so the
        // mark + spinner + caption sit at TRUE viewport centre, not
        // shifted down by the global Layout's navbar offset.
        //
        // Earlier the default used `flex-1 min-h-0` which fills the
        // parent flex container — fine in theory, but the parent in
        // Fitting's golfer routes has pt-[calc(4rem+env(safe-area-inset-top))]
        // for the navbar, so flex-1 ends up shorter than the viewport
        // by 4-5rem on top and items-center within that space lands
        // visibly BELOW screen centre. User-reported on the iOS
        // SFSafariViewController popover where the discrepancy is
        // most obvious.
        //
        // `fixed inset-0 z-50` puts the loader at full viewport,
        // independent of the navbar offset. The brand bg colour is
        // applied via inline style below so the overlay reads as one
        // continuous navy field with the splash / status bar.
        //
        // `inline` mode stays in-flow with py-16 — used by Pro admin
        // tabs and nested layouts where a fullscreen takeover would
        // hide the surrounding navigation.
        inline ? 'py-16' : 'fixed inset-0 z-50',
        className,
      )}
      style={
        inline
          ? undefined
          : {
              backgroundColor: 'var(--color-page-bg)',
              color: 'var(--color-on-surface)',
            }
      }
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <img
        src="/Asset%207.svg"
        alt=""
        aria-hidden
        className="h-20 w-auto max-w-[min(280px,80vw)] object-contain"
      />
      <div className="w-8 h-8 border-2 border-brand-500/40 border-t-[var(--color-brand)] rounded-full animate-spin" />
      <p
        className="text-sm text-center"
        style={{ color: 'var(--color-on-subtle)' }}
      >
        {message}
      </p>
    </div>
  );
}
